import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import {
  campaignAudienceFn,
  campaignProviderFn,
  campaignRecipientCountFn,
  getCampaignFn,
  listEmailTemplatesFn,
  saveCampaignFn,
  scheduleCampaignFn,
  sendCampaignFn,
  sendTestEmailFn,
} from "@/lib/campaigns/campaigns.functions";
import { renderCampaignHtml } from "@/lib/campaigns/template";
import {
  BLANK_CAMPAIGN,
  CAMPAIGN_AUDIENCE_LABEL,
  CAMPAIGN_STATUS_LABEL,
  CAMPAIGN_TEMPLATES,
  CAMPAIGN_TYPE_LABEL,
  isEditable,
  type CampaignAudience,
  type CampaignInput,
  type CampaignType,
} from "@/lib/campaigns/types";

export const Route = createFileRoute("/admin/campaigns/$id")({ component: CampaignEditor });

const CARD = "border border-black/10 bg-white p-4";
const FIELD = "w-full border border-black/15 px-3 py-2 text-sm focus:border-black/50 focus:outline-none";
const LABEL = "text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50";
const BTN = "border border-black/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em]";

function splitSchedule(iso: string | null) {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

function CampaignEditor() {
  const { id } = Route.useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const campaignQuery = useQuery({
    queryKey: ["campaigns", "detail", id],
    queryFn: () => getCampaignFn({ data: { id } }),
    enabled: !isNew,
  });
  const audienceQuery = useQuery({
    queryKey: ["campaigns", "audience"],
    queryFn: () => campaignAudienceFn(),
  });
  const providerQuery = useQuery({
    queryKey: ["campaigns", "provider"],
    queryFn: () => campaignProviderFn(),
  });
  const providerReady = providerQuery.data?.configured ?? false;

  const [form, setForm] = useState<CampaignInput>(BLANK_CAMPAIGN);
  const [loaded, setLoaded] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [dark, setDark] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [emailsRaw, setEmailsRaw] = useState("");

  const current = campaignQuery.data ?? null;
  const status = current?.status ?? "draft";
  const editable = isNew || isEditable(status);

  useEffect(() => {
    if (isNew || loaded || !current) return;
    setForm({
      id: current.id,
      name: current.name,
      type: current.type,
      subject: current.subject,
      previewText: current.previewText,
      template: current.template,
      featuredImage: current.featuredImage,
      content: current.content,
      ctaLabel: current.ctaLabel,
      ctaUrl: current.ctaUrl,
      audience: current.audience,
      audienceIds: current.audienceIds,
      audienceTags: current.audienceTags,
      audienceEmails: current.audienceEmails,
      scheduledAt: current.scheduledAt,
    });
    setEmailsRaw(current.audienceEmails.join("\n"));
    const s = splitSchedule(current.scheduledAt);
    setDate(s.date);
    setTime(s.time);
    setLoaded(true);
  }, [current, isNew, loaded]);

  const set = <K extends keyof CampaignInput>(key: K, value: CampaignInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const scheduledIso = useMemo(() => {
    if (!date) return null;
    const iso = new Date(`${date}T${time || "09:00"}`);
    return Number.isNaN(iso.getTime()) ? null : iso.toISOString();
  }, [date, time]);

  const save = useMutation({
    mutationFn: (input: CampaignInput) => saveCampaignFn({ data: input }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success("Campaign saved");
      if (isNew) void navigate({ to: "/admin/campaigns/$id", params: { id: r.id } });
    },
    onError: (e: Error) => toast.error(e.message || "Could not save the campaign"),
  });

  const persist = async () => {
    const payload = { ...form, scheduledAt: scheduledIso };
    const saved = await saveCampaignFn({ data: payload });
    void qc.invalidateQueries({ queryKey: ["campaigns"] });
    return saved.id;
  };

  const schedule = useMutation({
    mutationFn: async () => {
      if (!scheduledIso) throw new Error("Pick a schedule date and time");
      const campaignId = await persist();
      return scheduleCampaignFn({ data: { id: campaignId, scheduledAt: scheduledIso } }).then(
        () => campaignId,
      );
    },
    onSuccess: (campaignId) => {
      void qc.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success("Campaign scheduled");
      if (isNew) void navigate({ to: "/admin/campaigns/$id", params: { id: campaignId } });
    },
    onError: (e: Error) => toast.error(e.message || "Could not schedule"),
  });

  const sendNow = useMutation({
    mutationFn: async () => {
      const campaignId = await persist();
      return sendCampaignFn({ data: { id: campaignId } }).then((r) => ({ ...r, campaignId }));
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(`Sent ${r.sent} · failed ${r.failed} · remaining ${r.remaining}`);
      if (isNew) void navigate({ to: "/admin/campaigns/$id", params: { id: r.campaignId } });
    },
    onError: (e: Error) => toast.error(e.message || "Send failed"),
  });

  const test = useMutation({
    mutationFn: () => sendTestEmailFn({ data: { campaign: form, to: testTo } }),
    onSuccess: (r) =>
      r.ok
        ? toast.success(`Accepted by ${r.providerLabel ?? r.provider} — sent to ${testTo}`)
        : toast.error(r.pendingReason || r.error || "Test send failed"),
    onError: (e: Error) => toast.error(e.message || "Test send failed"),
  });

  const previewHtml = useMemo(
    () =>
      renderCampaignHtml({
        subject: form.subject || "Your subject line",
        previewText: form.previewText,
        template: form.template,
        featuredImage: form.featuredImage,
        content: form.content || "Write your campaign content…",
        ctaLabel: form.ctaLabel,
        ctaUrl: form.ctaUrl,
        eyebrow: CAMPAIGN_TYPE_LABEL[form.type],
      }),
    [form],
  );

  const subscribers = audienceQuery.data?.subscribers ?? [];
  const allTags = audienceQuery.data?.tags ?? [];

  const templatesQuery = useQuery({
    queryKey: ["campaigns", "templates"],
    queryFn: () => listEmailTemplatesFn(),
  });

  const countQuery = useQuery({
    queryKey: [
      "campaigns",
      "recipient-count",
      form.audience,
      form.audienceIds,
      form.audienceTags,
      form.audienceEmails,
    ],
    queryFn: () =>
      campaignRecipientCountFn({
        data: {
          audience: form.audience,
          audienceIds: form.audienceIds,
          audienceTags: form.audienceTags,
          audienceEmails: form.audienceEmails,
        },
      }),
  });
  const recipientCount = countQuery.data?.count ?? 0;

  function applyTemplate(t: {
    subject: string;
    previewText: string;
    template: string;
    content: string;
    ctaLabel: string;
    ctaUrl: string;
    featuredImage: string;
    campaignType: CampaignType;
    name: string;
  }) {
    setForm((f) => ({
      ...f,
      name: f.name || t.name,
      type: t.campaignType,
      subject: t.subject,
      previewText: t.previewText,
      template: t.template,
      content: t.content,
      ctaLabel: t.ctaLabel,
      ctaUrl: t.ctaUrl,
      featuredImage: t.featuredImage,
    }));
    toast.success(`Template “${t.name}” applied`);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isNew ? "New campaign" : form.name || "Campaign"}
        subtitle={isNew ? "Compose a campaign for your subscribers." : `Status: ${CAMPAIGN_STATUS_LABEL[status]}`}
        action={
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/campaigns" className={BTN}>Back</Link>
            {editable && (
              <button
                type="button"
                className={`${BTN} bg-black text-white`}
                disabled={save.isPending}
                onClick={() => save.mutate({ ...form, scheduledAt: scheduledIso })}
              >
                Save draft
              </button>
            )}
          </div>
        }
      />

      {!editable && (
        <div className={CARD}>
          <StatusPill tone="info">Read only</StatusPill>
          <span className="ml-3 text-sm text-black/60">
            A campaign that is sending or completed can no longer be edited. Duplicate it to make changes.
          </span>
        </div>
      )}

      {providerQuery.isSuccess && !providerReady && (
        <div className={CARD}>
          <StatusPill tone="warn">Provider not configured</StatusPill>
          <span className="ml-3 text-sm text-black/60">
            Provider not configured. Campaign will not send until credentials are added. You can still
            create, save and schedule campaigns.
          </span>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,520px)]">
        <div className="space-y-4">
          <section className={`${CARD} space-y-3`}>
            <p className={LABEL}>Campaign</p>
            {editable && (templatesQuery.data?.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-2 border-b border-black/10 pb-3">
                <span className="text-xs text-black/45">Start from a template:</span>
                {(templatesQuery.data ?? []).map((t) => (
                  <button key={t.id} type="button" className={BTN} onClick={() => applyTemplate(t)}>
                    {t.name}
                  </button>
                ))}
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={LABEL}>Campaign name</span>
                <input className={FIELD} value={form.name} disabled={!editable}
                  onChange={(e) => set("name", e.target.value)} />
              </label>
              <label className="block">
                <span className={LABEL}>Campaign type</span>
                <select className={FIELD} value={form.type} disabled={!editable}
                  onChange={(e) => set("type", e.target.value as CampaignType)}>
                  {Object.entries(CAMPAIGN_TYPE_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </label>
            </div>
            <label className="block">
              <span className={LABEL}>Subject</span>
              <input className={FIELD} value={form.subject} disabled={!editable}
                onChange={(e) => set("subject", e.target.value)} />
            </label>
            <label className="block">
              <span className={LABEL}>Preview text</span>
              <input className={FIELD} value={form.previewText} disabled={!editable}
                onChange={(e) => set("previewText", e.target.value)} />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={LABEL}>Email template</span>
                <select className={FIELD} value={form.template} disabled={!editable}
                  onChange={(e) => set("template", e.target.value)}>
                  {CAMPAIGN_TEMPLATES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className={LABEL}>Featured image URL</span>
                <input className={FIELD} value={form.featuredImage} disabled={!editable}
                  onChange={(e) => set("featuredImage", e.target.value)} />
              </label>
            </div>
          </section>

          <section className={`${CARD} space-y-3`}>
            <p className={LABEL}>Content</p>
            <textarea
              className={`${FIELD} min-h-[220px] font-mono text-[13px]`}
              value={form.content}
              disabled={!editable}
              onChange={(e) => set("content", e.target.value)}
              placeholder={"## Section heading\n\nParagraph text with **bold**, *italic* and [links](https://cb16.in)."}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={LABEL}>CTA button</span>
                <input className={FIELD} value={form.ctaLabel} disabled={!editable}
                  onChange={(e) => set("ctaLabel", e.target.value)} />
              </label>
              <label className="block">
                <span className={LABEL}>Button URL</span>
                <input className={FIELD} value={form.ctaUrl} disabled={!editable}
                  onChange={(e) => set("ctaUrl", e.target.value)} />
              </label>
            </div>
          </section>

          <section className={`${CARD} space-y-3`}>
            <p className={LABEL}>Audience</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {(
                [
                  "all_subscribers",
                  "all_active",
                  "imported",
                  "tags",
                  "selected",
                  "emails",
                  "test",
                ] as CampaignAudience[]
              ).map((a) => (
                <label key={a} className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={form.audience === a} disabled={!editable}
                    onChange={() => set("audience", a)} />
                  {CAMPAIGN_AUDIENCE_LABEL[a]}
                </label>
              ))}
            </div>

            <div className="flex items-center gap-2 border border-black/10 bg-[#faf9f7] px-3 py-2 text-sm">
              <span className={LABEL}>Recipients</span>
              <span className="text-base font-semibold text-black">
                {countQuery.isFetching ? "…" : recipientCount.toLocaleString("en-IN")}
              </span>
              <span className="text-xs text-black/45">
                {form.audience === "test"
                  ? "Test sends go only to the address below."
                  : "Live count for the current selection."}
              </span>
            </div>

            {form.audience === "tags" && (
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => {
                  const on = form.audienceTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      disabled={!editable}
                      className={`${BTN} ${on ? "bg-black text-white" : ""}`}
                      onClick={() =>
                        set(
                          "audienceTags",
                          on ? form.audienceTags.filter((t) => t !== tag) : [...form.audienceTags, tag],
                        )
                      }
                    >
                      {tag}
                    </button>
                  );
                })}
                {allTags.length === 0 && <p className="text-sm text-black/50">No tags yet.</p>}
              </div>
            )}

            {form.audience === "emails" && (
              <label className="block">
                <span className={LABEL}>Email addresses (one per line or comma separated)</span>
                <textarea
                  className={`${FIELD} min-h-[120px] font-mono text-[13px]`}
                  value={emailsRaw}
                  disabled={!editable}
                  onChange={(e) => {
                    setEmailsRaw(e.target.value);
                    set(
                      "audienceEmails",
                      e.target.value
                        .split(/[\s,;]+/)
                        .map((x) => x.trim().toLowerCase())
                        .filter((x) => x.includes("@")),
                    );
                  }}
                />
              </label>
            )}

            {form.audience === "selected" && (
              <div className="max-h-56 overflow-y-auto border border-black/10 p-2">
                {subscribers.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      disabled={!editable}
                      checked={form.audienceIds.includes(s.id)}
                      onChange={(e) =>
                        set(
                          "audienceIds",
                          e.target.checked
                            ? [...form.audienceIds, s.id]
                            : form.audienceIds.filter((x) => x !== s.id),
                        )
                      }
                    />
                    <span className="text-black/70">{s.email}</span>
                  </label>
                ))}
                {subscribers.length === 0 && <p className="p-2 text-sm text-black/50">No active subscribers.</p>}
              </div>
            )}
          </section>

          <section className={`${CARD} space-y-3`}>
            <p className={LABEL}>Schedule &amp; dispatch</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={LABEL}>Schedule date</span>
                <input type="date" className={FIELD} value={date} disabled={!editable}
                  onChange={(e) => setDate(e.target.value)} />
              </label>
              <label className="block">
                <span className={LABEL}>Schedule time</span>
                <input type="time" className={FIELD} value={time} disabled={!editable}
                  onChange={(e) => setTime(e.target.value)} />
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={BTN} disabled={!editable || schedule.isPending}
                onClick={() => schedule.mutate()}>
                Schedule campaign
              </button>
              <button
                type="button"
                className={`${BTN} bg-[#C6A15B] text-white`}
                disabled={!editable || sendNow.isPending || form.audience === "test"}
                onClick={() => {
                  const ok = window.confirm(
                    `Send “${form.subject || form.name || "this campaign"}” to ${recipientCount.toLocaleString("en-IN")} recipient(s) now? This cannot be undone.`,
                  );
                  if (ok) sendNow.mutate();
                }}
              >
                {sendNow.isPending ? "Sending…" : "Send immediately"}
              </button>
            </div>
            {current?.lastError && (
              <p className="text-xs text-red-700">Last error: {current.lastError}</p>
            )}
          </section>

          <section className={`${CARD} space-y-3`}>
            <p className={LABEL}>Send test email</p>
            <div className="flex flex-wrap gap-2">
              <input className={`${FIELD} max-w-xs`} placeholder="you@example.com" value={testTo}
                onChange={(e) => setTestTo(e.target.value)} />
              <button type="button" className={BTN} disabled={test.isPending || !testTo.includes("@") || !providerReady}
                onClick={() => test.mutate()}>
                {test.isPending ? "Sending…" : "Send test"}
              </button>
            </div>
            {test.data && (
              <ul className="space-y-1 text-xs">
                {test.data.attempts.map((a, i) => (
                  <li key={`${a.provider}-${i}`} className={a.ok ? "text-emerald-700" : "text-red-700"}>
                    {a.providerLabel}: {a.ok ? "accepted the message" : a.error}
                  </li>
                ))}
                {test.data.pendingReason && (
                  <li className="text-amber-700">{test.data.pendingReason}</li>
                )}
              </ul>
            )}
            {providerQuery.isSuccess && !providerReady && (
              <p className="text-xs text-black/45">
                Test sending requires a configured email provider.
              </p>
            )}
          </section>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className={LABEL}>Preview</p>
            <div className="flex gap-2">
              {(["desktop", "mobile"] as const).map((d) => (
                <button key={d} type="button" onClick={() => setDevice(d)}
                  className={`${BTN} ${device === d ? "bg-black text-white" : ""}`}>
                  {d}
                </button>
              ))}
              <button type="button" onClick={() => setDark((v) => !v)}
                className={`${BTN} ${dark ? "bg-black text-white" : ""}`}>
                dark
              </button>
            </div>
          </div>
          <div className={`border border-black/10 p-3 ${dark ? "bg-[#111111]" : "bg-[#f7f6f3]"}`}>
            <iframe
              title="Campaign preview"
              srcDoc={previewHtml}
              className="mx-auto h-[720px] border border-black/10 bg-white"
              style={{
                width: device === "desktop" ? "100%" : 380,
                filter: dark ? "invert(1) hue-rotate(180deg)" : undefined,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
